const defaultAutoplayOptions = {
  active: true,
  breakpoints: {},
  delay: 5000,
  jump: false,
  playOnInit: true,
  stopOnInteraction: true,
  stopOnMouseEnter: false,
  stopOnLastSnap: false,
  rootNode: null,
};

function EmblaCarouselAutoplay(userOptions = {}) {
  let options;
  let emblaApi;
  let interaction;
  let timer = 0;
  let jump = false;
  let rootNode;
  let enabled = true;
  let currentSlideVideo;
  let currentSlideImage;
  let currentSlideNeedsLoad;
  let startTime;
  let progress = 0;
  let currentDelay;

  function init(emblaApiInstance, optionsHandler) {
    emblaApi = emblaApiInstance;

    const { mergeOptions, optionsAtMedia } = optionsHandler;
    const optionsBase = mergeOptions(
      defaultAutoplayOptions,
      EmblaCarouselAutoplay.globalOptions
    );
    const allOptions = mergeOptions(optionsBase, userOptions);
    options = optionsAtMedia(allOptions);

    jump = options.jump;
    interaction = options.stopOnInteraction ? destroy : stop;

    const { eventStore, ownerDocument, ownerWindow } =
      emblaApi.internalEngine();
    const emblaRoot = emblaApi.rootNode();
    const root = (options.rootNode && options.rootNode(emblaRoot)) || emblaRoot;

    rootNode = root;

    emblaApi.on("pointerDown", interaction);
    if (!options.stopOnInteraction) emblaApi.on("pointerUp", reset);

    if (options.stopOnMouseEnter) {
      eventStore.add(root, "mouseenter", interaction);
      if (!options.stopOnInteraction) eventStore.add(root, "mouseleave", reset);
    }

    eventStore.add(ownerDocument, "visibilitychange", () => {
      if (ownerDocument.visibilityState === "hidden") return stop();
      reset();
    });
    eventStore.add(ownerWindow, "pagehide", (event) => {
      if (event.persisted) stop();
    });

    if (options.playOnInit) play();
  }

  function getSelectedSlide() {
    return emblaApi.slideNodes()[emblaApi.selectedScrollSnap()];
  }

  function destroy() {
    emblaApi.off("pointerDown", interaction);
    if (!options.stopOnInteraction) emblaApi.off("pointerUp", reset);

    stop();
    timer = 0;
  }

  function animateProgress(timeStamp) {
    if (!enabled) {
      return;
    }
    if (typeof startTime === "undefined") {
      startTime = timeStamp;
    }

    let slideDuration = currentDelay;
    const lastProgress = progress;
    progress = 0;

    if (currentSlideVideo) {
      slideDuration = currentSlideVideo.duration;
      const elapsed = currentSlideVideo.currentTime;
      progress = elapsed / slideDuration;
    } else {
      const elapsed = timeStamp - startTime;
      progress = elapsed / slideDuration;
    }

    rootNode.style.setProperty(
      "--embla-autoplay-progress",
      progress * 100 + "%"
    );
    if (progress >= 1 || progress < lastProgress) {
      next();
    } else {
      timer = requestAnimationFrame(animateProgress);
    }
  }
  function play(jumpOverride) {
    stop();
    if (!enabled) {
      return;
    }
    if (typeof jumpOverride !== "undefined") jump = jumpOverride;

    const selectedSlide = getSelectedSlide();

    if (currentSlideNeedsLoad) {
      currentSlideNeedsLoad = false;
      currentSlideImage.removeEventListener("load", handleSlideImageLoad);
    }

    currentSlideVideo = selectedSlide
      ? selectedSlide.querySelector("video")
      : undefined;
    currentSlideImage = currentSlideVideo
      ? undefined
      : selectedSlide.querySelector("img");
    const selectedSlideDelay =
      selectedSlide &&
      selectedSlide.dataset.autoplayDelay &&
      parseInt(selectedSlide.dataset.autoplayDelay);
    currentDelay =
      selectedSlideDelay > 1000 ? selectedSlideDelay : options.delay;

    progress = 0;
    rootNode.style.setProperty("--embla-autoplay-progress", "0%");
    startTime = undefined;

    if (
      currentSlideImage &&
      currentSlideImage.src &&
      (!currentSlideImage.complete || currentSlideImage.naturalWidth === 0)
    ) {
      currentSlideNeedsLoad = true;
      currentSlideImage.addEventListener("load", handleSlideImageLoad);
    } else {
      timer = window.requestAnimationFrame(animateProgress);
    }

    rootNode.classList.add("embla--autoplay-active");
  }

  function handleSlideImageLoad(event) {
    if (event.currentTarget === currentSlideImage) {
      play();
    }
  }

  function stop() {
    if (!timer) return;
    window.cancelAnimationFrame(timer);
    rootNode.classList.remove("embla--autoplay-active");
  }

  function reset() {
    if (!timer) return;
    stop();
    play();
  }

  function next() {
    const { index } = emblaApi.internalEngine();
    const lastIndex = emblaApi.scrollSnapList().length - 1;
    const kill = options.stopOnLastSnap && index.get() === lastIndex;

    if (kill) return destroy();

    if (emblaApi.canScrollNext()) {
      emblaApi.scrollNext(jump);
    } else {
      emblaApi.scrollTo(0, jump);
    }
    play();
  }

  function setEnabled(value) {
    enabled = value;

    if (enabled) {
      play();
    } else {
      stop();
    }
  }

  const self = {
    name: "autoplay",
    options: userOptions,
    init,
    destroy,
    play,
    stop,
    reset,
    setEnabled,
  };
  return self;
}
